import { useEffect, useMemo, useRef, useState } from 'react';
import { isDbRecordId } from '../../../db/odin-db-util';
import useFormPermissions from '../../../hooks/use-form-permissions';
import { useGetRecordPermissionsQuery } from '../../../redux/services/record';
import { extractRecordPermissions } from '../../../utils/form-permissions-util';

/**
 * A hook that handles record permissions and edit/read mode.
 *
 * @param {string} [recordId] - The record id.
 * @param {number} [formId] - The form id.
 * @param {boolean} [openInEditMode=false] - Indicates if the record should open in edit mode.
 *
 * @returns {{
 *   recordPanelReadOnlyMode: boolean,
 *   canEditRecord: boolean | undefined, // undefined while permissions are loading
 *   formPermissions: FormPermissionsResults,
 *   isFormPermissionsLoading: boolean,
 *   recordPermissions: FormPermissionsResults,
 *   isRecordPermissionLoading: boolean,
 *   currentRecordPermissions: CurrentRecordPermisions,
 *   onRecordsEdit: () => void
 * }}
 */
const useRecordPermissionsAndMode = (recordId?: string | number, formId?: number, openInEditMode: boolean = false) => {
  // Also detect temp (negative) IDs in WebView where isDbRecordId is unavailable
  const isDbRecord = isDbRecordId(recordId);
  const isOfflineRecord = isDbRecord || (!!window.ReactNativeWebView && typeof recordId === 'number' && recordId < 0);
  const shouldStartInEditMode = openInEditMode || isOfflineRecord;

  //   Initial read-only mode logic (XOR pattern):
  //   - New records (no recordId) start in edit mode for data entry
  //   - Existing records start in read mode for viewing
  //   - shouldStartInEditMode=true overrides the default behavior
  const [recordPanelReadOnlyMode, setRecordPanelReadOnlyMode] = useState(Boolean(recordId) !== shouldStartInEditMode); // simulate logical XOR

  const editButtonClickedRef = useRef(Boolean(recordId) === shouldStartInEditMode);

  const { formPermissions, isLoading: isFormPermissionsLoading } = useFormPermissions(formId);

  const { data: recordPermissions, isFetching: isRecordPermissionLoading } = useGetRecordPermissionsQuery(
    { recordId: recordId ? Number(recordId) : undefined },
    { skip: recordId === undefined || isOfflineRecord }
  );

  const currentRecordPermissions = extractRecordPermissions(recordPermissions);

  // Compute if editable, only when necessary dependencies change
  const canEditRecord = useMemo(() => {
    if (isOfflineRecord) return true;

    if (isFormPermissionsLoading || isRecordPermissionLoading) return undefined;

    // New record (no recordId) — depends only on form-level permission
    if (!recordId) {
      return formPermissions?.canEditRecord ?? false;
    }

    // Existing record — depends on both form and record permissions
    return (
      ((formPermissions?.canEditRecord && currentRecordPermissions?.edit) || recordPermissions?.canEditRecord) ?? false
    );
  }, [
    isOfflineRecord,
    isFormPermissionsLoading,
    isRecordPermissionLoading,
    recordId,
    formPermissions?.canEditRecord,
    currentRecordPermissions?.edit,
    recordPermissions?.canEditRecord,
  ]);

  const onRecordsEdit = () => {
    setRecordPanelReadOnlyMode(false);
    editButtonClickedRef.current = true;
  };

  useEffect(() => {
    if (canEditRecord === undefined) return; // still loading

    const shouldBeReadOnly = !(canEditRecord && editButtonClickedRef.current);
    setRecordPanelReadOnlyMode(shouldBeReadOnly);
  }, [canEditRecord]);

  // If shouldStartInEditMode is true and permissions allow, automatically enter edit mode once loaded
  useEffect(() => {
    if (canEditRecord && shouldStartInEditMode) {
      setRecordPanelReadOnlyMode(false);
    }
  }, [canEditRecord, shouldStartInEditMode]);

  return {
    recordPanelReadOnlyMode,
    canEditRecord,
    formPermissions,
    isFormPermissionsLoading,
    recordPermissions,
    isRecordPermissionLoading,
    currentRecordPermissions,
    onRecordsEdit,
  };
};

export default useRecordPermissionsAndMode;
